import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { captureAttribution } from "@/lib/analytics/dataLayer";
import { startAnalytics, stopAnalytics, trackEvent } from "@/lib/booking/analytics";
import { captureFbclid, sendCapiAttribution } from "@/lib/analytics/capiAttribution";

/**
 * Global page-view tracker. Mounted once inside <BrowserRouter> so it runs on
 * every route change — not just the booking funnel. This is what feeds the
 * pricing-events backend the session-scoped UTM / gclid / fbclid / landing_url
 * data that downstream analytics_sessions needs.
 *
 * captureAttribution() re-runs on every navigation so a UTM landing on
 * /cabanas?gclid=... is picked up the same as a hit on /.
 */
export function usePageTracking(): void {
  const location = useLocation();
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    startAnalytics();
    // Meta ad attribution: capture fbclid before in-app navigation strips
    // query params, then beacon ~2s later so the Pixel has written _fbp.
    captureFbclid();
    const capiTimer = window.setTimeout(() => sendCapiAttribution(), 2000);
    return () => {
      stopAnalytics();
      startedRef.current = false;
      window.clearTimeout(capiTimer);
    };
  }, []);

  useEffect(() => {
    captureAttribution();
    trackEvent("page_view", {
      landing_url: typeof window !== "undefined" ? window.location.href : "",
      path: location.pathname,
    });
  }, [location.pathname, location.search]);
}
